import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import { sleep } from 'utils/javascript';
import { PayerError } from '@payer-admin-ui/components/src/utils/errors';
import { toPayerError } from 'utils/rtk';
import { BankIdClient, BankIdStatus, sessionAPI } from './sessionAPI';
import { setSession } from './session';
import { loadUserSettings } from './userSettings';
import { MerchantAdminDispatch, MerchantAdminState } from './store';

const BANKID_REFRESH_INTERVAL = 1000;

export type LoginBankIdState = {
  bankIdReference: string | null;
  qrData: string | null;
  clients: BankIdClient[];
  error: PayerError | null;
  urlPending: boolean;
};

export const startBankIdAuthentication = createAsyncThunk<
  { qrData: string; bankIdReference: string },
  void,
  { dispatch: MerchantAdminDispatch }
>('login/bankid/authenticate', async (_, { dispatch }) => {
  const { qrData, reference: bankIdReference } = await sessionAPI.startBankIdAuthentication();
  dispatch(bankIdPollStatus());
  return {
    qrData,
    bankIdReference,
  };
});

export const bankIdPollStatus = createAsyncThunk<
  void,
  void,
  { state: MerchantAdminState; dispatch: MerchantAdminDispatch }
>('login/bankid/pollStatus', async (_, { dispatch, getState }) => {
  let response: { status: BankIdStatus; qrData?: string } | undefined;
  do {
    await sleep(BANKID_REFRESH_INTERVAL);
    const bankIdReference = getState().login.bankId.bankIdReference as string | null;
    if (isEmpty(bankIdReference)) return;
    if (bankIdReference) response = await sessionAPI.getBankIdStatus(bankIdReference);
    if (response && !isEmpty(response.qrData)) dispatch(updateQrData(response.qrData as string));
  } while (response && (response.status === 'PENDING' || response.status === 'USER_SIGNING'));
  if (!response) return;
  switch (response.status) {
    case 'FAILED':
      throw new PayerError({ code: 'bankid.failed' });
    case 'CONSUMED':
      throw new PayerError({ code: 'bankid.consumed' });
    case 'TIMED_OUT':
      return;
    case 'USER_CANCELLED':
      return;
    case 'COMPLETED':
      dispatch(bankIdLoginWithClient({ clientId: null }));
      return;
    default:
      throw new PayerError({
        code: 'bankid.unknown',
        detail: `Unrecognized bankid status, response: '${String(response)}'.`,
      });
  }
});

export const bankIdLoginWithClient = createAsyncThunk<
  Partial<LoginBankIdState>,
  { clientId: string | null },
  { state: MerchantAdminState; dispatch: MerchantAdminDispatch }
>('login/bankid/login', async ({ clientId }, { dispatch, getState }) => {
  const { bankIdReference } = getState().login.bankId;
  if (!bankIdReference) return { ...initialState };
  const { clients = [], session } = await sessionAPI.loginBankId({
    clientId,
    bankIdReference,
  });
  if (session) {
    dispatch(setSession(session));
    dispatch(loadUserSettings());
    return { ...initialState };
  } else if (isEmpty(clients)) {
    throw new PayerError({ code: 'bankid.errorNoClients' });
  } else {
    return { clients };
  }
});

const initialState: LoginBankIdState = {
  bankIdReference: null,
  qrData: null,
  clients: [],
  error: null,
  urlPending: false,
};

const loginBankIdSlice = createSlice({
  name: 'login/bankid',
  initialState,
  reducers: {
    clear: () => ({ ...initialState }),
    resetError: state => {
      state.error = null;
    },
    updateQrData: (state, action: { payload: string }) => {
      state.qrData = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(startBankIdAuthentication.pending, () => ({ ...initialState, urlPending: true }));
    builder.addCase(startBankIdAuthentication.fulfilled, (state, { payload: { qrData, bankIdReference } }) => ({
      ...initialState,
      qrData,
      bankIdReference,
    }));
    builder.addCase(startBankIdAuthentication.rejected, (state, { error }) => ({
      ...initialState,
      error: toPayerError(error),
    }));

    builder.addCase(bankIdPollStatus.fulfilled, state => ({ ...state, qrData: null }));
    builder.addCase(bankIdPollStatus.rejected, (state, { error }) => ({ ...initialState, error: toPayerError(error) }));

    builder.addCase(bankIdLoginWithClient.fulfilled, (state, { payload }) => ({ ...state, ...payload }));
    builder.addCase(bankIdLoginWithClient.rejected, (state, { error }) => ({
      ...initialState,
      error: toPayerError(error),
    }));
  },
});

const { actions, reducer } = loginBankIdSlice;
const { updateQrData } = actions;
export const { clear, resetError } = actions;
export { reducer as loginBankIdReducer };
